import { z } from 'zod'

import {
  type ConstraintValidator,
  type LadderPolicyConstraint,
  ModelConstraintType,
  type ValidationResult,
} from '@/types/api/constraints'

import { constraintPrioritySchema } from './helpers'

/**
 * Zod schema for ladder policy constraint validation
 */
export const ladderPolicyConstraintSchema = z
  .object({
    type: z.literal(ModelConstraintType.LadderPolicy),
    period_length: z.number().positive('Period length must be greater than 0'),
    start_period: z
      .number()
      .min(0, 'Start period must be greater than or equal to 0')
      .default(0),
    end_period: z
      .number()
      .positive('End period must be greater than 0')
      .default(1),
    grace_period_months: z
      .number()
      .min(0, 'Grace period months must be greater than or equal to 0')
      .default(1),
    weight_tolerance: z.number().min(0).max(1).optional(),
    market_value_tolerance: z.number().optional(),
    constraint_priority: constraintPrioritySchema,
    use_call_adjusted_maturity: z.boolean().default(false),
    id: z.string().default(''),
  })
  .refine((data) => !(data.end_period <= data.start_period), {
    message: 'End period must be greater than start period',
    path: ['end_period'],
  }) as z.ZodType<LadderPolicyConstraint>

/**
 * Validate tolerance - either weight_tolerance or market_value_tolerance must be specified, but not both
 */
function validateToleranceConsistency(
  weightTolerance?: number,
  marketValueTolerance?: number,
): { valid: boolean; error?: string; errors?: string[] } {
  const errors: string[] = []

  if (weightTolerance !== undefined && marketValueTolerance !== undefined) {
    errors.push(
      'Market value tolerance and weight tolerance cannot be specified together',
    )
  }

  if (weightTolerance === undefined && marketValueTolerance === undefined) {
    errors.push(
      'Either market value tolerance or weight tolerance must be specified',
    )
  }

  return {
    valid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : undefined,
    errors: errors.length > 0 ? errors : undefined,
  }
}

/**
 * Handle num_periods if provided
 * This replicates the suboptimal @model_validator(mode="before") parse_num_periods logic
 * https://github.com/moment-technology/suboptimal/blob/main/suboptimal/types/optimization.py
 */
export function processNumPeriods<T extends Partial<LadderPolicyConstraint>>(
  constraint: T,
): T {
  const result = { ...constraint }

  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  const resultAny = result as any
  if (resultAny.num_periods !== undefined) {
    resultAny.end_period = (resultAny.start_period || 0) + resultAny.num_periods
    delete resultAny.num_periods
  }

  return result
}

/**
 * Validates a ladder policy constraint
 */
export function validateLadderPolicyConstraint(
  constraint: LadderPolicyConstraint,
): ValidationResult {
  try {
    const errors: string[] = []

    // Process num_periods if it exists
    const processedConstraint = processNumPeriods(constraint)

    // Validate using Zod schema
    const result = ladderPolicyConstraintSchema.safeParse(processedConstraint)
    if (!result.success) {
      // Get all zod errors
      result.error.errors.forEach((err) => {
        errors.push(err.message)
      })

      return {
        valid: false,
        errors,
      }
    }

    const { weight_tolerance, market_value_tolerance } = processedConstraint

    // Validate tolerance consistency
    const toleranceValidation = validateToleranceConsistency(
      weight_tolerance,
      market_value_tolerance,
    )

    if (!toleranceValidation.valid && toleranceValidation.errors) {
      errors.push(...toleranceValidation.errors)
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown validation error'

    return {
      valid: false,
      errors: [errorMessage],
    }
  }
}

/**
 * Validator for ladder policy constraints
 */
export const ladderPolicyValidator: ConstraintValidator<LadderPolicyConstraint> =
  {
    validate: validateLadderPolicyConstraint,
    schema: ladderPolicyConstraintSchema,
  }
